<template>
  <div v-if="data.length === 0 && !loading" class="p-6">
    <h4 class="p-2 rounded-lg bg-surface-200 dark:bg-surface-700">No Data</h4>
  </div>
  <div v-else class="p-2 pb-0 h-full">
    <DataTable
      lazy
      :value="data"
      :loading="loading"
      paginator
      size="medium"
      sort-mode="single"
      :first="offset"
      :rows="limit"
      :sort-field="sortField"
      :sort-order="sortOrder"
      :rows-per-page-options="[10, 20, 50, 100, 200]"
      :total-records="queryStore.results.total_rows"
      table-style="min-width: 50rem"
      scrollable
      scroll-height="flex"
      :current-page-report-template="
        '{first} to {last} of ' +
        queryStore.results.total_rows?.toLocaleString()
      "
      :paginator-template="'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport'"
      @page="onPageChange"
      @update:rows="limit = $event"
    >
      <Column
        v-for="col in headers"
        :key="col.field"
        :sortable="true"
        :field="col.field"
        :header="col.header"
        :style="{ minWidth: getColumnWidth(col.field) + 'px' }"
      >
        <template #sorticon>
          <div class="relative">
            <i
              :class="getIconClass(col.field)"
              class="icon"
              @click="toggleDialog(col.field)"
            ></i>
            <i
              v-if="sortField === col.field"
              :class="getSortIconClass(sortOrder)"
              class="sort-indicator"
              @click="handleLazySort({ sortField: col.field, sortOrder: 0 })"
            ></i>
            <div
              v-if="dialogToggle[col.field]"
              :class="[
                'dialog-box',
                { 'dialog-box-expanded': pivotOptions[col.field] },
              ]"
              class="bg-surface-100 dark:bg-surface-800 overflow-auto"
            >
              <button
                class="dialog-button hover:bg-primary-200 dark:hover:bg-primary-700"
                @click="handleLazySort({ sortField: col.field, sortOrder: 1 })"
              >
                <i class="pi pi-sort-amount-up"></i> Sort Ascending
              </button>
              <button
                class="dialog-button hover:bg-primary-200 dark:hover:bg-primary-700"
                @click="handleLazySort({ sortField: col.field, sortOrder: -1 })"
              >
                <i class="pi pi-sort-amount-down"></i> Sort Descending
              </button>
              <button
                class="dialog-button hover:bg-primary-200 dark:hover:bg-primary-700"
                @click="togglePivotOptions(col.field)"
              >
                <i class="pi pi-table"></i> Pivot Column
              </button>
              <div v-if="pivotOptions[col.field]" class="pivot-options">
                <hr class="divider" />
                <label for="pivot-row">Values:</label>
                <Select
                  id="pivot-row"
                  v-model="pivotValue"
                  :options="
                    headers
                      .filter((h) => h.field !== col.field)
                      .map((header) => header.field)
                  "
                >
                  {{ header.header }}
                </Select>
                <label for="pivot-row">Operator:</label>
                <Select
                  id="pivot-row"
                  v-model="pivotOperator"
                  :options="['SUM', 'MAX', 'AVG', 'MIN', 'COUNT']"
                >
                </Select>
                <button
                  class="dialog-button hover:bg-primary-200 dark:hover:bg-primary-700"
                  @click="runPivot"
                >
                  <i class="pi pi-play"></i> Run Pivot
                </button>
              </div>
            </div>
          </div>
        </template>
        <template #body="slotProps">
          {{ slotProps.data[col.field]?.toLocaleString() }}
        </template>
      </Column>
      <template #paginatorstart>
        <Button
          v-if="isPivoted"
          variant="link"
          class="p-button p-component"
          @click="resetTable"
        >
          <span class="p-button-label">Reset Table</span>
        </Button>
      </template>
      <template #paginatorend>
        <div style="visibility: hidden">
          <Button v-if="isPivoted" variant="link" class="p-button p-component">
            <span class="p-button-label">Reset Table</span>
          </Button>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script setup>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Select from "primevue/select";
import axiosRepo from "@/repositories/AxiosRepository";
import { computed, onMounted, ref, watch } from "vue";
import { useQueryStore } from "@/stores/queryStore.js";

const queryStore = useQueryStore();
const dialogToggle = ref({});
const pivotOptions = ref({});
const pivotColumn = ref(null);
const pivotValue = ref(null);
const pivotOperator = ref(null);
const isPivoted = ref(null);

function getIconClass(field) {
  return dialogToggle.value[field] ? "pi pi-chevron-up" : "pi pi-chevron-down";
}

let tablePath = ref(queryStore.results.table_path || "");

watch(
  () => queryStore.results.table_path,
  (newPath) => {
    tablePath.value = newPath || "";
  }
);

function getSortIconClass(order) {
  return order === 0
    ? ""
    : order === 1
    ? "pi pi-sort-amount-up"
    : "pi pi-sort-amount-down";
}

function toggleDialog(field) {
  dialogToggle.value = {
    ...dialogToggle.value,
    [field]: !dialogToggle.value[field],
  };
}

function togglePivotOptions(field) {
  pivotOptions.value = {
    ...pivotOptions.value,
    [field]: !pivotOptions.value[field],
  };
  pivotColumn.value = field;
}

async function runPivot() {
  loading.value = true;
  updatePathPivot();
}

const page = ref(0);
const limit = ref(10);
const offset = computed(() => Number(limit.value * page.value));
const sortField = ref(null);
const sortOrder = ref(0);
const loading = ref(false);

const data = ref([]);

onMounted(async () => {
  await getResults();
});

watch(tablePath, async () => {
  if (tablePath.value === "") return;
  await getResults();
});

watch([sortField, sortOrder], async () => {
  if (tablePath.value === "") return;
  await updatePathSort();
  await getResults();
});

watch([offset, limit], async () => {
  if (tablePath.value === "") return;
  await getResults();
});

watch(page, async () => {
  if (tablePath.value === "") return;
  await getResults();
});

async function updatePathSort() {
  let path = await axiosRepo.get_results_path({
    table_path: tablePath.value,
    sort_field: sortField.value,
    sort_order: sortOrder.value,
  });
  if (path.status === 200) {
    tablePath.value = path.data.table_path;
  }
}

async function updatePathPivot() {
  loading.value = true;
  let path = await axiosRepo.get_results_path({
    table_path: tablePath.value,
    pivotColumn: pivotColumn.value,
    pivotValues: pivotValue.value,
    pivotOperator: pivotOperator.value,
  });
  if (path.status === 200) {
    isPivoted.value = true;
    tablePath.value = path.data.table_path;
    getResults();
  }
}

async function getResults() {
  if (tablePath.value !== "") {
    loading.value = true;
    let res = await axiosRepo.get_results(
      tablePath.value,
      offset.value,
      limit.value
    );
    if (res.status === 200) {
      data.value = res.data;
      if (data.value.length > 0) {
        queryStore.resultCols = Object.keys(res.data[0]);
      } else {
        queryStore.resultCols = [];
      }
    }
    dialogToggle.value = {};
    loading.value = false;
  }
}

const headers = computed(() => {
  if (queryStore.resultCols.length === 0) return [];
  return queryStore.resultCols.map((key) => {
    return { field: key, header: key };
  });
});

async function handleLazySort(event) {
  if (event.sortOrder === 0) {
    resetTable();
  }
  sortField.value = event.sortField;
  sortOrder.value = event.sortOrder;
}

function onPageChange(event) {
  page.value = event.page;
}

const additionalWidth = computed(() => {
  return (field) => {
    return sortField.value === field &&
      (sortOrder.value === 1 || sortOrder.value === -1)
      ? 40
      : 0;
  };
});

function getColumnWidth(field) {
  const textLength =
    headers.value.find((header) => header.field === field)?.header.length || 0;
  return textLength + 80 + additionalWidth.value(field); // Adjust the multiplier and additional pixels as needed
}

async function resetTable() {
  tablePath.value = queryStore.results.table_path;
  await getResults();
}
</script>

<style scoped>
.dialog-box {
  position: absolute;
  top: 50%;
  transform: translate(0%, 16px);
  padding: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
  width: 14rem; /* Adjust the width as needed */
  height: 150px;
}

.dialog-box-expanded {
  height: 220px;
}

.icon {
  position: absolute;
  top: 50%;
  transform: translate(0%, -50%);
  padding: 0.25rem;
  z-index: 20;
}

.sort-indicator {
  position: absolute;
  top: 50%;
  transform: translate(0%, -50%);
  padding-left: 2rem;
  z-index: 5;
}

.dialog-button {
  display: block;
  width: 100%;
  padding: 0.5rem;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
}

.pivot-options {
  margin-top: 1rem;
  padding: 0.5rem;
}

.pivot-options label {
  display: block;
  margin-bottom: 0.5rem;
}

.pivot-options select {
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 1rem;
  background: rgba(240, 240, 240, 0.54);
}

.divider {
  margin: 0.5rem 0;
  border: 0;
  border-top: 1px solid #ccc;
}
</style>
